import {
  type AriaAttributes,
  type CSSProperties,
  type FocusEventHandler,
  type KeyboardEventHandler,
  type PointerEventHandler,
  type ReactNode,
  type Ref,
} from 'react'
import { Button as AriaButton } from 'react-aria-components'
import {
  AnimatePresence,
  LazyMotion,
  m,
  type Transition,
  type Variants as MotionVariants,
} from 'motion/react'
import cx from 'classnames'
import { Portal } from '@/components/Portal'
import styles from './Snackbar.module.css'
import { IconButton } from '@/components/IconButton'
import type { PressEventHandler } from '@/infra/prop-types'

const snackbarTransition: MotionVariants = {
  initial: { y: 40, opacity: 0 },
  animate: { y: 0, opacity: 1 },
  exit: { y: -10, opacity: 0 },
}

const snackbarIconTransitions: MotionVariants = {
  initial: { opacity: 0, scale: 0.8 },
  exit: { opacity: 0, scale: 0.7 },
  animate: { opacity: 1, scale: 1 },
}

const snackbarIconSpring: Transition = {
  type: 'spring',
  stiffness: 700,
  damping: 25,
}

interface SnackbarViewportProps {
  children?: ReactNode

  /**
   * Accessible label.
   */
  label?: string

  /**
   * A CSS value for offsetting the viewport from the bottom.
   */
  offset?: number | string
}

/**
 * Snackbar Viewport
 */
export function SnackbarViewport({
  children,
  label = 'Notifications',
  offset,
}: SnackbarViewportProps) {
  return (
    <Portal>
      <div
        role="region"
        className={styles.viewport}
        aria-label={label}
        style={{ '--snackbar-viewport-offset': offset } as CSSProperties}
        // Ensures that the snackbars are treated as being in the top layer by
        // React Aria. This prevents interacting with snackbars from closing
        // modals.
        data-react-aria-top-layer={true}
      >
        {children}
      </div>
    </Portal>
  )
}

const loadMotionFeatures = () =>
  import('@/infra/motion-features').then((r) => r.default)

interface SnackbarListProps {
  children?: ReactNode
}

/**
 * List of snackbars.
 */
export function SnackbarList({ children }: SnackbarListProps) {
  return (
    <LazyMotion features={loadMotionFeatures}>
      <m.ol layoutRoot tabIndex={-1} className={cx(styles.list)}>
        <AnimatePresence mode="popLayout">{children}</AnimatePresence>
      </m.ol>
    </LazyMotion>
  )
}

export interface SnackbarProps {
  children?: ReactNode
  ref?: Ref<HTMLLIElement>

  /**
   * Focus event handler.
   */
  onFocus?: FocusEventHandler<HTMLLIElement>

  /**
   * Blur event handler.
   */
  onBlur?: FocusEventHandler<HTMLLIElement>

  /**
   * Pointer down event handler.
   */
  onPointerDown?: PointerEventHandler<HTMLLIElement>

  /**
   * Key-down event handler.
   */
  onKeyDown?: KeyboardEventHandler<HTMLLIElement>

  /**
   * Prevent screen readers from announcing changes when `true`.
   */
  'aria-busy'?: AriaAttributes['aria-busy']

  /**
   * Controls the sensitivity of the snackbar for accessibility purposes.
   * "Assertive" snackbars will be announced immediately, while "polite" ones
   * will wait for the next opportune moment.
   */
  'aria-live'?: AriaAttributes['aria-live']

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Snackbars provide brief messages about processes at the
 * bottom of the screen.
 */
export function Snackbar({
  children,
  onFocus,
  onBlur,
  onPointerDown,
  onKeyDown,
  ref,
  testId,
  'aria-live': ariaLive = 'assertive',
}: SnackbarProps) {
  return (
    <m.li
      data-test={testId}
      layout
      ref={ref}
      tabIndex={0}
      aria-atomic
      aria-relevant="all"
      aria-live={ariaLive}
      role="status"
      className={cx(styles.item)}
      variants={snackbarTransition}
      onFocus={onFocus}
      onBlur={onBlur}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {children}
    </m.li>
  )
}

interface SnackbarContentProps {
  children?: ReactNode
}

/**
 * Snackbar Content. Contains the headline and description.
 */
export function SnackbarContent({ children }: SnackbarContentProps) {
  return (
    <m.div layout="preserve-aspect" className={styles.content}>
      {children}
    </m.div>
  )
}

interface SnackbarHeadlineProps {
  children?: ReactNode
}

/**
 * Snackbar Headline
 */
export function SnackbarHeadline({ children }: SnackbarHeadlineProps) {
  return <m.div className={styles.headline}>{children}</m.div>
}

interface SnackbarDescriptionProps {
  children?: ReactNode
}

/**
 * Snackbar Description
 */
export function SnackbarDescription({ children }: SnackbarDescriptionProps) {
  return <m.div className={styles.description}>{children}</m.div>
}

interface SnackbarIconProps {
  children?: ReactNode
  animationKey?: string
}

/**
 * Snackbar Icon
 */
export function SnackbarIcon({ animationKey, children }: SnackbarIconProps) {
  return (
    <div className={styles.icon}>
      <AnimatePresence mode="popLayout">
        <m.div
          variants={snackbarIconTransitions}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={snackbarIconSpring}
          className={styles.iconContent}
          key={animationKey}
        >
          {children}
        </m.div>
      </AnimatePresence>
    </div>
  )
}

interface SnackbarCloseButtonProps {
  onPress?: PressEventHandler
  testId?: string
}

/**
 * Snackbar Close Button
 */
export function SnackbarCloseButton({
  onPress,
  testId,
}: SnackbarCloseButtonProps) {
  return (
    <m.div layout className={styles.closeButton}>
      <IconButton
        name="close"
        onPress={onPress}
        label="Dismiss"
        testId={testId}
      />
    </m.div>
  )
}

interface SnackbarActionsProps {
  children?: ReactNode
}

/**
 * Snackbar Actions
 */
export function SnackbarActions({ children }: SnackbarActionsProps) {
  return (
    <m.ul className={styles.actions} aria-label="Actions">
      {children}
    </m.ul>
  )
}

interface SnackbarActionProps {
  label: string
  onPress?: PressEventHandler
  testId?: string
}

/**
 * Snackbar Action Button
 */
export function SnackbarAction({
  label,
  onPress,
  testId,
}: SnackbarActionProps) {
  return (
    <m.li layout className={styles.action}>
      <AriaButton
        data-test={testId}
        onPress={onPress}
        className={({ isPressed, isHovered, isFocusVisible }) =>
          cx(
            styles.actionButton,
            isPressed && styles.pressed,
            isHovered && styles.hover,
            isFocusVisible && styles.focusVisible,
          )
        }
      >
        {label}
      </AriaButton>
    </m.li>
  )
}
